import React, { useEffect, useState } from "react";
import {
  ArrowRight,
  Bot,
  CheckCircle2,
  ClipboardCheck,
  Gift,
} from "lucide-react";
import { Link } from "react-router-dom";

const tasks = [
  {
    number: "1",
    label: "Receive Task",
    title: "Read Instructions",
    description:
      "Each task outlines explicit campaign criteria, dispatch lists, and required proof.",
    icon: ClipboardCheck,
    color: "emerald",
  },
  {
    number: "2",
    label: "Execute in Software",
    title: "Perform Cloud Actions",
    description:
      "Configure bot replies, trigger delivery batches, or test automation proxies.",
    icon: Bot,
    color: "blue",
  },
  {
    number: "3",
    label: "Admin Review & Reward",
    title: "Wallet Credit Upon Approval",
    description:
      "Our review team audits proof submissions and credits the task reward immediately.",
    icon: Gift,
    color: "purple",
  },
];

function Tasks() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoaded(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  return (
    <main className="min-h-screen overflow-hidden bg-[#f6f8fb] text-slate-900">
      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden">
        {/* Background glow */}
        <div className="pointer-events-none absolute -left-40 top-10 h-80 w-80 animate-[taskFloat_8s_ease-in-out_infinite] rounded-full bg-emerald-200/30 blur-[120px]" />

        <div className="pointer-events-none absolute -right-40 top-20 h-96 w-96 animate-[taskFloat_10s_ease-in-out_infinite_reverse] rounded-full bg-blue-100/40 blur-[130px]" />

        <div className="pointer-events-none absolute left-0 right-0 top-0 h-px bg-slate-200" />

        <div
          className={`relative mx-auto max-w-7xl px-5 pb-10 pt-12 text-center transition-all duration-1000 sm:px-6 sm:pb-14 sm:pt-16 lg:px-8 lg:pt-20 ${
            loaded
              ? "translate-y-0 opacity-100"
              : "translate-y-8 opacity-0"
          }`}
        >
          {/* Refrex */}
          <div className="mb-5 inline-flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#00a96d] shadow-md shadow-emerald-200">
              <span className="text-xs font-black text-white">R</span>
            </div>

            <span className="text-[10px] font-black uppercase tracking-[0.18em] text-emerald-600">
              Referx
            </span>
          </div>

          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-600">
            Productivity & Campaigns
          </p>

          <h1 className="mt-3 text-4xl font-black leading-tight tracking-[-0.05em] text-[#08111f] sm:text-5xl lg:text-6xl">
            The Assigned Task Framework
          </h1>

          <p className="mx-auto mt-5 max-w-2xl text-sm leading-6 text-slate-500 sm:text-base">
            Your work starts inside the WhatsApp Automation Software.
            Complete assigned campaigns and track approvals in real time.
          </p>
        </div>
      </section>

      {/* ================= TASK CARDS ================= */}
      <section className="relative">
        <div className="pointer-events-none absolute left-1/2 top-0 h-96 w-96 -translate-x-1/2 rounded-full bg-emerald-100/30 blur-[130px]" />

        <div className="relative mx-auto max-w-7xl px-5 pb-12 sm:px-6 sm:pb-16 lg:px-8">
          <div className="grid gap-5 md:grid-cols-3">
            {tasks.map((task, index) => {
              const Icon = task.icon;

              return (
                <div
                  key={task.number}
                  className={`group relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_10px_35px_rgba(15,23,42,0.05)] transition-all duration-700 hover:-translate-y-2 hover:border-emerald-200 hover:shadow-[0_24px_55px_rgba(15,23,42,0.10)] ${
                    loaded
                      ? "translate-y-0 opacity-100"
                      : "translate-y-10 opacity-0"
                  }`}
                  style={{
                    transitionDelay: `${index * 150 + 200}ms`,
                  }}
                >
                  {/* Animated top border */}
                  <div
                    className={`absolute left-0 right-0 top-0 h-1 origin-left scale-x-0 transition-transform duration-500 group-hover:scale-x-100 ${
                      task.color === "emerald"
                        ? "bg-emerald-500"
                        : task.color === "blue"
                        ? "bg-blue-500"
                        : "bg-purple-500"
                    }`}
                  />

                  <div className="p-6 sm:p-7">
                    {/* Label */}
                    <div
                      className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 ${
                        task.color === "emerald"
                          ? "bg-emerald-100 text-emerald-700"
                          : task.color === "blue"
                          ? "bg-blue-100 text-blue-700"
                          : "bg-purple-100 text-purple-700"
                      }`}
                    >
                      <span className="text-[9px] font-black">
                        {task.number}. {task.label}
                      </span>
                    </div>

                    {/* Icon */}
                    <div className="mt-6 flex items-center justify-between">
                      <div
                        className={`flex h-11 w-11 items-center justify-center rounded-xl transition-all duration-500 group-hover:scale-110 group-hover:rotate-3 ${
                          task.color === "emerald"
                            ? "bg-emerald-50 text-emerald-600"
                            : task.color === "blue"
                            ? "bg-blue-50 text-blue-600"
                            : "bg-purple-50 text-purple-600"
                        }`}
                      >
                        <Icon size={20} strokeWidth={1.8} />
                      </div>

                      <span className="text-5xl font-black tracking-[-0.08em] text-slate-100 transition-colors duration-300 group-hover:text-slate-200">
                        {task.number}
                      </span>
                    </div>

                    {/* Content */}
                    <h2 className="mt-5 text-xl font-black tracking-[-0.03em] text-[#08111f]">
                      {task.title}
                    </h2>

                    <p className="mt-3 text-sm leading-6 text-slate-500">
                      {task.description}
                    </p>

                    {/* Status */}
                    <div className="mt-6 flex items-center gap-2 border-t border-slate-100 pt-5">
                      <CheckCircle2
                        size={14}
                        className="text-emerald-500"
                      />

                      <span className="text-[10px] font-semibold text-slate-400">
                        Task workflow
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= CTA ================= */}
      <section className="relative overflow-hidden bg-[#08111f]">
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-80 w-80 -translate-x-1/2 -translate-y-1/2 animate-pulse rounded-full bg-emerald-500/10 blur-[110px]" />

        <div
          className={`relative mx-auto max-w-3xl px-5 py-12 text-center transition-all duration-1000 sm:px-6 sm:py-16 lg:px-8 ${
            loaded
              ? "translate-y-0 opacity-100"
              : "translate-y-8 opacity-0"
          }`}
          style={{ transitionDelay: "650ms" }}
        >
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-400/10 text-emerald-400">
            <ClipboardCheck size={21} />
          </div>

          <h2 className="mt-5 text-2xl font-black tracking-[-0.04em] text-white sm:text-3xl">
            Start Completing Tasks
          </h2>

          <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-slate-400">
            Access assigned campaigns and manage your work through the Referx
            platform.
          </p>

          <Link
            to="/signup"
            className="group mt-6 inline-flex min-h-12 items-center justify-center rounded-xl bg-[#00a96d] px-6 py-3 text-sm font-black text-white shadow-lg shadow-emerald-950/30 transition-all duration-300 hover:-translate-y-1 hover:bg-[#00945f] hover:shadow-xl"
          >
            Get Started

            <ArrowRight
              size={16}
              className="ml-2 transition-transform duration-300 group-hover:translate-x-1"
            />
          </Link>
        </div>
      </section>

      <style>{`
        @keyframes taskFloat {
          0%,
          100% {
            transform: translate3d(0, 0, 0) scale(1);
          }

          50% {
            transform: translate3d(0, -18px, 0) scale(1.04);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          *,
          *::before,
          *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
    </main>
  );
}

export default Tasks;